import React, { useState } from 'react';
import { Share2, Check, Copy, Linkedin, Twitter } from 'lucide-react';
import { toast } from 'sonner';
import { getFrontendUrl } from '../utils/urlConfig';

/**
 * Premium share button with dropdown for copy + social handoff.
 *
 * Props:
 *   - kind: 'job' | 'profile' | 'certificate' | 'contest'
 *   - id: resource id (job id, username, certificate number, contest id)
 *   - label: button label (default: 'Share')
 *   - variant: 'solid' | 'outline' | 'ghost' (default: 'outline')
 *   - size: 'sm' | 'md' (default: 'md')
 *   - testid: data-testid override
 */
export const ShareButton = ({
  kind,
  id,
  label = 'Share',
  variant = 'outline',
  size = 'md',
  testid,
}) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Always build share URLs against the public EvalentX domain so links posted
  // on social platforms never leak preview/internal hostnames. The backend
  // share endpoint (/api/share/...) lives on the same domain as the frontend
  // in production, so this proxies to the OG-meta wrapper correctly.
  const shareUrl = `${getFrontendUrl()}/api/share/${kind}/${encodeURIComponent(id)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Share link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy. Long-press the link to share manually.');
    }
  };

  const tryNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ url: shareUrl });
        return true;
      } catch {
        return false;
      }
    }
    return false;
  };

  const onClick = async (e) => {
    e.stopPropagation();
    const shared = await tryNativeShare();
    if (shared) return;
    setOpen((v) => !v);
  };

  const onLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'noopener,noreferrer'
    );
    setOpen(false);
  };

  const onTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'noopener,noreferrer'
    );
    setOpen(false);
  };

  const sizeClass = size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm';
  const baseClass = `inline-flex items-center gap-2 font-bold tracking-wide transition-all ${sizeClass}`;
  const variantClass = {
    solid: 'bg-[#B91C1C] text-white hover:bg-[#7F1D1D]',
    outline: 'border border-[#E5E5E5] text-[#0A0A0A] bg-white hover:border-[#B91C1C] hover:text-[#B91C1C]',
    ghost: 'text-[#525252] hover:text-[#B91C1C] hover:bg-[#FEF2F2]',
  }[variant] || '';

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={onClick}
        className={`${baseClass} ${variantClass}`}
        style={{ borderRadius: '2px' }}
        data-testid={testid || `share-${kind}-btn`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Share2 size={14} />
        {label}
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Close share menu"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            className="absolute right-0 top-full mt-2 w-60 bg-white border z-50 overflow-hidden"
            style={{
              borderColor: '#E5E5E5',
              borderRadius: '2px',
              boxShadow: '0 12px 32px rgba(10,10,10,0.12)',
            }}
            data-testid={`share-${kind}-menu`}
          >
            <button
              type="button"
              onClick={copyLink}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-left text-[#0A0A0A] hover:bg-[#FEF2F2] transition-colors"
              data-testid={`share-${kind}-copy`}
            >
              {copied ? (
                <Check size={14} className="text-[#059669]" />
              ) : (
                <Copy size={14} className="text-[#525252]" />
              )}
              <span className="flex-1 font-medium">{copied ? 'Copied' : 'Copy link'}</span>
            </button>
            <button
              type="button"
              onClick={onLinkedIn}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-left text-[#0A0A0A] hover:bg-[#FEF2F2] transition-colors border-t"
              style={{ borderColor: '#F5F5F5' }}
              data-testid={`share-${kind}-linkedin`}
            >
              <Linkedin size={14} className="text-[#525252]" />
              <span className="flex-1 font-medium">Share to LinkedIn</span>
            </button>
            <button
              type="button"
              onClick={onTwitter}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-left text-[#0A0A0A] hover:bg-[#FEF2F2] transition-colors border-t"
              style={{ borderColor: '#F5F5F5' }}
              data-testid={`share-${kind}-twitter`}
            >
              <Twitter size={14} className="text-[#525252]" />
              <span className="flex-1 font-medium">Share to X</span>
            </button>
            <div className="px-3.5 py-2 text-[10px] tracking-[0.15em] uppercase font-bold text-[#A3A3A3] bg-[#FAFAF9] border-t" style={{ borderColor: '#F5F5F5' }}>
              Preview · 1200×630
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ShareButton;
